import { Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { lazy, Suspense, useEffect } from 'react';
import { Center, Loader } from '@mantine/core';
import { AuthProvider } from '@/modules/auth/AuthProvider';
import { ProtectedRoute } from '@/modules/auth/ProtectedRoute';
import { LoginPage } from '@/modules/auth/LoginPage';
import { OAuthCallbackPage } from '@/modules/auth/OAuthCallbackPage';
import { AppShell } from '@/components/layout/AppShell';
import { ErrorBoundary } from '@/components/ErrorBoundary';
import { trackEvent } from '@/lib/analytics/analytics-client';
import { ANALYTICS_EVENTS } from '@/lib/analytics/events';

const DashboardPage = lazy(() =>
  import('@/modules/dashboard/DashboardPage').then((m) => ({ default: m.DashboardPage }))
);
const UsersListPage = lazy(() =>
  import('@/modules/users').then((m) => ({ default: m.UsersListPage }))
);
const CompaniesListPage = lazy(() =>
  import('@/modules/companies').then((m) => ({ default: m.CompaniesListPage }))
);
const TenantThreeSixtyPage = lazy(() =>
  import('@/modules/tenant-360').then((m) => ({ default: m.TenantThreeSixtyPage }))
);
const AuditListPage = lazy(() =>
  import('@/modules/audit').then((m) => ({ default: m.AuditListPage }))
);
const SettingsPage = lazy(() =>
  import('@/modules/settings').then((m) => ({ default: m.SettingsPage }))
);
const AppStoreListPage = lazy(() =>
  import('@/modules/appstore').then((m) => ({ default: m.AppStoreListPage }))
);
const CustomerSupportTicketsPage = lazy(() =>
  import('@/modules/customer-support').then((m) => ({ default: m.CustomerSupportTicketsPage }))
);
const CustomerSupportCategoriesPage = lazy(() =>
  import('@/modules/customer-support').then((m) => ({ default: m.CustomerSupportCategoriesPage }))
);
const CustomerSupportQueuesPage = lazy(() =>
  import('@/modules/customer-support').then((m) => ({ default: m.CustomerSupportQueuesPage }))
);
const CustomerSupportReportsPage = lazy(() =>
  import('@/modules/customer-support').then((m) => ({ default: m.CustomerSupportReportsPage }))
);
const GodAdminWorkspacesPage = lazy(() =>
  import('@/modules/customer-support').then((m) => ({ default: m.GodAdminWorkspacesPage }))
);
const ProductUpdatesPage = lazy(() =>
  import('@/modules/crm').then((m) => ({ default: m.ProductUpdatesPage }))
);
const SubscriptionCustomProductsPage = lazy(() =>
  import('@/modules/subscription-management').then((m) => ({
    default: m.SubscriptionCustomProductsPage,
  }))
);
const SubscriptionManagementPage = lazy(() =>
  import('@/modules/subscription-management').then((m) => ({
    default: m.SubscriptionManagementPage,
  }))
);
const SubscriptionPlansPage = lazy(() =>
  import('@/modules/subscription-management').then((m) => ({
    default: m.SubscriptionPlansPage,
  }))
);

function PageLoader() {
  return (
    <Center h="100%">
      <Loader size="lg" />
    </Center>
  );
}

function App() {
  const location = useLocation();

  useEffect(() => {
    trackEvent(ANALYTICS_EVENTS.PAGE_VIEWED, {
      page_name: location.pathname,
    });
  }, [location]);

  return (
    <AuthProvider>
      <Routes>
        <Route path="/login" element={<LoginPage />} />
        <Route path="/oauth/code/google" element={<OAuthCallbackPage />} />
        <Route
          path="/"
          element={
            <ProtectedRoute>
              <AppShell />
            </ProtectedRoute>
          }
        >
          <Route index element={<Navigate to="/dashboard" replace />} />
          <Route
            path="dashboard"
            element={
              <ErrorBoundary>
                <Suspense fallback={<PageLoader />}>
                  <DashboardPage />
                </Suspense>
              </ErrorBoundary>
            }
          />
          <Route
            path="users"
            element={
              <ErrorBoundary>
                <Suspense fallback={<PageLoader />}>
                  <UsersListPage />
                </Suspense>
              </ErrorBoundary>
            }
          />
          <Route
            path="companies"
            element={
              <ErrorBoundary>
                <Suspense fallback={<PageLoader />}>
                  <CompaniesListPage />
                </Suspense>
              </ErrorBoundary>
            }
          />
          <Route
            path="companies/:companyId/360"
            element={
              <ErrorBoundary>
                <Suspense fallback={<PageLoader />}>
                  <TenantThreeSixtyPage />
                </Suspense>
              </ErrorBoundary>
            }
          />
          <Route
            path="audit"
            element={
              <ErrorBoundary>
                <Suspense fallback={<PageLoader />}>
                  <AuditListPage />
                </Suspense>
              </ErrorBoundary>
            }
          />
          <Route
            path="settings"
            element={
              <ErrorBoundary>
                <Suspense fallback={<PageLoader />}>
                  <SettingsPage />
                </Suspense>
              </ErrorBoundary>
            }
          />
          <Route
            path="appstore"
            element={
              <ErrorBoundary>
                <Suspense fallback={<PageLoader />}>
                  <AppStoreListPage />
                </Suspense>
              </ErrorBoundary>
            }
          />
          <Route
            path="crm/product-updates"
            element={
              <ErrorBoundary>
                <Suspense fallback={<PageLoader />}>
                  <ProductUpdatesPage />
                </Suspense>
              </ErrorBoundary>
            }
          />
          <Route
            path="subscriptions"
            element={<Navigate to="/subscriptions/companies" replace />}
          />
          <Route
            path="subscriptions/companies"
            element={
              <ErrorBoundary>
                <Suspense fallback={<PageLoader />}>
                  <SubscriptionManagementPage />
                </Suspense>
              </ErrorBoundary>
            }
          />
          <Route
            path="subscriptions/plans"
            element={
              <ErrorBoundary>
                <Suspense fallback={<PageLoader />}>
                  <SubscriptionPlansPage />
                </Suspense>
              </ErrorBoundary>
            }
          />
          <Route
            path="subscriptions/custom-products"
            element={
              <ErrorBoundary>
                <Suspense fallback={<PageLoader />}>
                  <SubscriptionCustomProductsPage />
                </Suspense>
              </ErrorBoundary>
            }
          />
          <Route path="support" element={<Navigate to="/support/tickets" replace />} />
          <Route
            path="support/tickets"
            element={
              <ErrorBoundary>
                <Suspense fallback={<PageLoader />}>
                  <CustomerSupportTicketsPage />
                </Suspense>
              </ErrorBoundary>
            }
          />
          <Route
            path="support/categories"
            element={
              <ErrorBoundary>
                <Suspense fallback={<PageLoader />}>
                  <CustomerSupportCategoriesPage />
                </Suspense>
              </ErrorBoundary>
            }
          />
          <Route
            path="support/queues"
            element={
              <ErrorBoundary>
                <Suspense fallback={<PageLoader />}>
                  <CustomerSupportQueuesPage />
                </Suspense>
              </ErrorBoundary>
            }
          />
          <Route
            path="support/reports"
            element={
              <ErrorBoundary>
                <Suspense fallback={<PageLoader />}>
                  <CustomerSupportReportsPage />
                </Suspense>
              </ErrorBoundary>
            }
          />
          <Route
            path="support/god-admin"
            element={
              <ErrorBoundary>
                <Suspense fallback={<PageLoader />}>
                  <GodAdminWorkspacesPage />
                </Suspense>
              </ErrorBoundary>
            }
          />
        </Route>
        <Route path="*" element={<Navigate to="/dashboard" replace />} />
      </Routes>
    </AuthProvider>
  );
}

export default App;
